import { useCallback, useState } from 'react'
import { apiPost } from 'tennders-stracture/libs/api'
import { toast } from 'react-toastify'
import { useToast } from 'tennders-common/hooks/useToast'
import useSocket from 'tennders-common/hooks/useSocket'
import useTransaction from 'tennders-transactions/hooks/useTransaction'
import useTranslation from 'next-translate/useTranslation'

const useMultipleSalesInvoiceSave = (props) => {
  const {
    lines,
    discountLines,
    extraCostsLines,
    form,
    invoiceDocuments,
    totalExtraCostsIncVat,
    totalDiscount,
    setFormErrors,
    totalEndAmountToPay,
    setLineErrors,
    setLineForm,
    calculations,
    selectedTransaction,
    onClose,
    setform,
    isCreditNote
  } = props
  const [loading, setLoading] = useState(false)
  const [approving, setApproving] = useState(false)
  const [withdrawing, setWithdrawing] = useState(false)
  const { toastSuccess } = useToast()
  const { setRefresh } = useSocket()
  const { refreshTransaction } = useTransaction()
  const { t } = useTranslation()

  const scrollToTop = () => {
    const elementModal = document.getElementsByClassName('modal')
    if (elementModal?.length) {
      elementModal[0].scrollIntoView({ behavior: 'smooth' })
    }
  }

  const onSuccess = useCallback(() => {
    if (selectedTransaction?.id) {
      refreshTransaction(selectedTransaction?.id)
    }
  }, [selectedTransaction])

  const save = async (createTemporaryDocument = true) => {
    try {
      const endpoint = isCreditNote ? '/creditNotes' : '/salesInvoice'
      const params = { ...form }
      delete params.oplog
      await apiPost(endpoint, {
        ...params,
        company: form.toCompany?.id,
        ...calculations,
        lines,
        discountLines,
        extraCostsLines,
        invoiceDocuments,
        totalExtraCostsIncVat,
        totalDiscount,
        totalAmount: totalEndAmountToPay,
        createTemporaryDocument
      })
    } catch (e) {
      const errors = e?.response?.data?.errors
      if (errors && Object.keys(errors)) {
        const _errors = errors
        if (_errors?.company_id) {
          _errors['invoiceNo'] = _errors.company_id
          toast.error(_errors.company_id)
        }
        setFormErrors(errors)
        scrollToTop()
        return false
      }
      return false
    }
    setLoading(false)

    return true
  }

  const handleSubmit = useCallback(async () => {
    setLoading(true)
    const result = await save()
    if (result) {
      toast.success((isCreditNote ? 'Credit Note' : 'Sales Invoice') + ' has been saved')
      setTimeout(() => onSuccess(), 500)
      onClose()
      setRefresh('all')
    }
    setLoading(false)
  }, [form.id, save])

  const handleApprove = useCallback(async () => {
    setApproving(true)
    const status = await save(false)
    if (status) {
      const endpoint = isCreditNote ? '/creditNotes' : '/salesInvoice'
      const data = await apiPost(`${endpoint}/approve/${form.id}`)
      if (data.status) {
        if (isCreditNote) {
          toastSuccess(t('creditNote:creditNoteApproved'))
        } else {
          toastSuccess(t('salesInvoice:salesInvoiceApproved'))
        }
        setform((prev) => ({
          ...prev,
          status: 'approved'
        }))
      }
    }
    setApproving(false)
    if (onClose) {
      onClose()
    }
    setRefresh('all')
  }, [form.id, save])

  const handleWithdraw = useCallback<(reason: string) => Promise<void>>(async (reason: string) => {
    setWithdrawing(true)

    const data = await apiPost(isCreditNote ? `/creditNotes/withdraw/${form.id}`
      : `/salesInvoice/withdraw/${form.id}`, { reason })
    if (data.status) {
      if (isCreditNote) {
        toastSuccess(t('creditNote:creditNoteWithdraw'))
      } else {
        toastSuccess(t('salesInvoice:salesInvoiceWithdraw'))
      }
      setform((prev) => ({ ...prev, status: 'withdrawed', approveHidden: true }))
    }

    setWithdrawing(false)
    setRefresh('all')
  }, [form.id, handleSubmit])

  return {
    save,
    handleApprove,
    handleSubmit,
    handleWithdraw,
    withdrawing,
    submitting: loading,
    approving
  }
}

export default useMultipleSalesInvoiceSave
